// Six one-digit boxes for a verification code — the same look as the login
// screen's MFA step (classes from App.css). Controlled: `value` holds the digits
// typed so far. onComplete fires once all six are in: typed, pasted, or dropped
// into the first box by the phone's "From Messages" autofill.
import React, { useRef } from 'react';

const LENGTH = 6;

export default function MfaCodeBoxes({
  value, onChange, onComplete, disabled = false, hasError = false, autoFocus = true,
}) {
  const boxes = useRef([]);

  const update = (code, focusIndex) => {
    const next = code.replace(/\D/g, '').slice(0, LENGTH);
    onChange(next);
    if (next.length === LENGTH) onComplete?.(next);
    else boxes.current[Math.min(focusIndex, LENGTH - 1)]?.focus();
  };

  const handleChange = (index, typed) => {
    let digits = typed.replace(/\D/g, '');
    if (!digits) {
      if (typed === '') onChange(value.slice(0, index));
      return;
    }
    // Typing into a filled box leaves the old digit beside the new one.
    if (digits.length === 2 && value[index]) {
      digits = digits[0] === value[index] ? digits[1] : digits[0];
    }
    update(value.slice(0, index) + digits, index + digits.length);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      event.preventDefault();
      onChange(value.slice(0, index - 1));
      boxes.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    update(event.clipboardData.getData('text'), LENGTH - 1);
  };

  return (
    <div className="mfa-code-input-container">
      {Array.from({ length: LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(el) => { boxes.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1} of ${LENGTH}`}
          className={`mfa-code-input ${value[index] ? 'filled' : ''} ${hasError ? 'error' : ''}`}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
        />
      ))}
    </div>
  );
}
